import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { t, tn } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';
import { Button, IconButton } from './ui.tsx';

// ---- reminders, on every screen ------------------------------------------------------------------------

export interface CalendarSoon {
  key: string;
  id: string;
  occ: string | null;
  name: string;
  starts: string;
  ends: string;
  location: string | null;
  video: string | null;
  remind_at: string;
}
const DISMISSED = 'erp.calendar.dismissed';

/** Shows a card when an event's reminder is due, until it is put away or the event is over. */
export function CalendarReminders({ soon }: { soon: CalendarSoon[] }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [gone, setGone] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(DISMISSED) ?? '[]');
    } catch {
      return [];
    }
  });
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(id);
  }, []);
  const dismiss = (k: string) => {
    const next = [...gone.slice(-40), k];
    setGone(next);
    try {
      localStorage.setItem(DISMISSED, JSON.stringify(next));
    } catch {
      // the card stays away for this visit
    }
  };
  const due = soon.filter((s) => Date.parse(s.remind_at) <= now && Date.parse(s.ends) > now && !gone.includes(`${s.key}@${s.remind_at}`)).slice(0, 3);
  if (!due.length) return null;
  return (
    <div className="cal-reminders" role="region" aria-label={t('cal.reminders')} aria-live="polite">
      {due.map((s) => {
        const mins = Math.round((Date.parse(s.starts) - now) / 60_000);
        const k = `${s.key}@${s.remind_at}`;
        return (
          <div key={k} className="cal-reminder">
            <span className="cal-reminder-icon" aria-hidden="true">
              <Icon name="bell" size={18} />
            </span>
            <div className="cal-reminder-main">
              <strong>{s.name}</strong>
              <span className="muted small">
                {mins > 0 ? tn('cal.starts_in', mins) : mins > -1 ? t('cal.starts_now') : t('cal.started')}
                {s.location ? ` · ${s.location}` : ''}
              </span>
              <div className="cal-reminder-acts">
                {s.video ? (
                  <a className="btn btn-primary btn-sm" href={s.video} target="_blank" rel="noopener noreferrer" onClick={() => dismiss(k)}>
                    <Icon name="video" size={16} />
                    <span className="btn-label">{t('cal.join_call')}</span>
                  </a>
                ) : null}
                <Button
                  size="sm"
                  onClick={() => {
                    dismiss(k);
                    navigate(`/calendar?e=${s.id}${s.occ ? `&o=${s.occ}` : ''}`);
                  }}
                >
                  {t('cal.open')}
                </Button>
              </div>
            </div>
            <IconButton icon="x" label={t('cal.dismiss')} onClick={() => dismiss(k)} />
          </div>
        );
      })}
    </div>
  );
}
